/**
 * In-app Tasks page (`/workspaces/:workspaceId/tasks`).
 *
 * Renders the prototype Task Feed inside the authenticated app shell. The driver
 * roster is real (live `driverRosterQuery`); the task + active-context payload is
 * demo data overlaid per driver. Reached from the left-nav "Tasks" button.
 */

import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import cuid from "cuid";
import { useCallback, useContext, useMemo, useState } from "react";

import { publishToWorkspaceFeed } from "@/models/actions/clientActions";
import { DataContext } from "@/models/DataProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery";
import { driverRosterQuery } from "@/models/repositories/Driver";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import type { DriverFilterConfig } from "@/types/driver";
import { buildDriverActivity, buildDriverThread, type FeedMessage } from "../boards";
import { accent, zinc } from "../colors";
import { DriverMessageView } from "../DriverMessageView";
import { buildDemoDrivers, buildDemoTasks, type RealDriverInput } from "../demo/overlay";
import { makeSeededEntry } from "../demo/seed";
import { TaskFeedView } from "../TaskFeedView";
import type { DispatchTask, Driver, DriverActivityEvent } from "../types";

interface OpenedDriver {
  id: string;
  name: string;
  truck: string;
  messages: FeedMessage[];
  activity: DriverActivityEvent[];
}

/** Show the whole roster, alphabetical — the task feed does its own filtering. */
const ROSTER_FILTER: DriverFilterConfig = {
  tab: "all",
  team: "",
  teamAliases: [],
  sort: "alphaAsc",
  statusFilter: [],
  dutyStatusFilter: [],
  groupIds: [],
  aliasNames: [],
  location: null,
  search: "",
  searchTargets: [{ kind: "column", field: "name" }],
  language: "en",
  unactivatedMembershipIds: [],
};

export default function TasksPage() {
  const { currentWorkspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { preferredLanguage } = useContext(DataContext);
  const [toast, setToast] = useState<string | null>(null);
  const [seeded, setSeeded] = useState<{ driver: Driver; task: DispatchTask }[]>([]);
  const [focusDriverId, setFocusDriverId] = useState<string>();
  const [openedDriver, setOpenedDriver] = useState<OpenedDriver | null>(null);

  const membershipId = myCurrentWorkspaceMembership?.id ?? "";
  const rosterRows =
    useReactiveSelectQuery(
      driverRosterQuery,
      [
        {
          workspaceId: currentWorkspaceId ?? "",
          membershipId,
          myCurrentWorkspaceMembershipId: membershipId,
          filter: ROSTER_FILTER,
        },
      ],
      "TasksPage:driverRosterQuery",
    ) ?? [];

  const realRows = useMemo<RealDriverInput[]>(
    () =>
      rosterRows.map((r) => ({
        workspaceMembershipId: r.workspaceMembershipId,
        name: r.name,
        alias: r.alias,
        feedId: r.feedId,
        accountId: r.accountId,
        location: r.location,
      })),
    [rosterRows],
  );

  const drivers = useMemo(() => [...seeded.map((s) => s.driver), ...buildDemoDrivers(realRows)], [seeded, realRows]);
  const tasks = useMemo(
    () => [...seeded.map((s) => s.task), ...buildDemoTasks(buildDemoDrivers(realRows))],
    [seeded, realRows],
  );

  const handleSeed = useCallback(() => {
    const entry = makeSeededEntry();
    setSeeded((prev) => [entry, ...prev]);
    setFocusDriverId(entry.driver.id);
    setToast(`Seeded a new task for ${entry.driver.name}`);
  }, []);

  // Real send: publishes a TTS message to the driver's live feed. Demo fallback
  // drivers (no real feed) resolve without publishing.
  const handleSendMessage = useCallback(
    async (driver: Driver, messageText: string) => {
      if (!currentWorkspaceId || !driver.feedId) return;
      await publishToWorkspaceFeed({
        workspaceId: currentWorkspaceId,
        feedId: driver.feedId,
        contentId: cuid(),
        text: messageText,
        preferredLanguage,
      });
    },
    [currentWorkspaceId, preferredLanguage],
  );

  // Unified message history: open the prototype's per-driver thread + activity
  // timeline in-place, the same view reached from the Live Feed and drivers view.
  const handleOpenMessageHistory = useCallback((driver: Driver) => {
    setOpenedDriver({
      id: driver.id,
      name: driver.name,
      truck: driver.truck,
      messages: buildDriverThread(driver.id),
      activity: buildDriverActivity(driver.id),
    });
  }, []);

  return (
    // The new-design content area is `position: relative` with no definite height,
    // so fill it absolutely — a `height: 100%` chain collapses and clips the feed.
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: zinc[950],
        minHeight: 0,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {openedDriver ? (
          <DriverMessageView
            key={openedDriver.id}
            driverName={openedDriver.name}
            truck={openedDriver.truck}
            messages={openedDriver.messages}
            activity={openedDriver.activity}
            onBack={() => setOpenedDriver(null)}
          />
        ) : (
          <TaskFeedView
            drivers={drivers}
            tasks={tasks}
            onToast={setToast}
            onSendMessage={handleSendMessage}
            onOpenMessageHistory={handleOpenMessageHistory}
            focusDriverId={focusDriverId}
            headerAction={
              <Box
                component="button"
                type="button"
                onClick={handleSeed}
                sx={{
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: 13,
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.875,
                  borderRadius: "8px",
                  color: "#fff",
                  bgcolor: accent.purple,
                  border: `1px solid ${accent.purple}`,
                  transition: "all 120ms ease",
                  "&:hover": { bgcolor: "#8a0fde", borderColor: "#8a0fde" },
                }}
              >
                + Seed data
              </Box>
            }
          />
        )}
      </Box>
      <Snackbar
        open={toast !== null}
        autoHideDuration={2600}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={toast ?? ""}
      />
    </Box>
  );
}
